import { useEffect, useState } from "react";

// NUCLEUS is the convergence point (center-left area)
const NUCLEUS = { x: 280, y: 190 };
// SQUEEKIT endpoint (far right) - line was 394px, now ~434px
const SQUEEKIT = { x: 720, y: 190 };
const BLUE = "#3C9CE6"; /* matches --accent-primary */
const BLUE_FAINT = "#C8DAFE"; /* light blue for spoke tips */
const DARK = "#111318";

// Each feature: tip = end of spoke (where the label lives), anchor = text alignment
const features = [
  {
    id: "pane",
    label: ["Pane Count"],
    tip: { x: 280, y: 48 },
    anchor: "middle",
    labelOffset: { x: 0, y: -10 },
  },
  {
    id: "pricing",
    label: ["Custom", "Pricing"],
    tip: { x: 100, y: 98 },
    anchor: "end",
    labelOffset: { x: -10, y: 0 },
  },
  {
    id: "customer",
    label: ["Customer", "Information"],
    tip: { x: 80, y: 265 },
    anchor: "end",
    labelOffset: { x: -10, y: 0 },
  },
  {
    id: "window",
    label: ["Window Types"],
    tip: { x: 210, y: 340 },
    anchor: "middle",
    labelOffset: { x: 0, y: 18 },
  },
  {
    id: "crm",
    label: ["CRM Integration"],
    tip: { x: 380, y: 320 },
    anchor: "middle",
    labelOffset: { x: 0, y: 18 },
  },
];

function dist(a, b) {
  return Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
}

export default function SqueekitDiagram() {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDrawn(true), 80);
    return () => clearTimeout(t);
  }, []);

  const gatherLen = dist(NUCLEUS, SQUEEKIT);

  return (
    <div className="squeekit-diagram">
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600&display=swap"
        rel="stylesheet"
      />

      <style>{`
        @keyframes drawLine {
          to { stroke-dashoffset: 0; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(5px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .squeekit-diagram .spoke {
          stroke-dasharray: var(--spoke-len);
          stroke-dashoffset: var(--spoke-len);
          animation: drawLine 0.45s ease forwards var(--spoke-delay);
          animation-play-state: ${drawn ? "running" : "paused"};
        }
        .squeekit-diagram .feat-label {
          opacity: 0;
          animation: fadeUp 0.35s ease forwards var(--label-delay);
          animation-play-state: ${drawn ? "running" : "paused"};
        }
        .squeekit-diagram .gather-line {
          stroke-dasharray: ${gatherLen};
          stroke-dashoffset: ${gatherLen};
          animation: drawLine 0.65s cubic-bezier(0.3,0,0.2,1) forwards 0.65s;
          animation-play-state: ${drawn ? "running" : "paused"};
        }
        .squeekit-diagram .nucleus-ring {
          opacity: 0;
          animation: fadeUp 0.3s ease forwards 0.6s;
          animation-play-state: ${drawn ? "running" : "paused"};
        }
        .squeekit-diagram .squeekit-word {
          opacity: 0;
          animation: fadeUp 0.45s ease forwards 1.4s;
          animation-play-state: ${drawn ? "running" : "paused"};
        }
      `}</style>

      <svg
        viewBox="0 0 780 388"
        width="100%"
        className="squeekit-diagram__svg"
        aria-label="Squeekit features converging diagram"
      >
        <defs>
          {/* Radial gradient for nucleus glow */}
          <radialGradient id="nucleusGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={BLUE} stopOpacity="0.13" />
            <stop offset="100%" stopColor={BLUE} stopOpacity="0" />
          </radialGradient>

          {/* Per-spoke linear gradients: bold blue at nucleus, fading at tip */}
          {features.map((f) => (
            <linearGradient
              key={`grad-${f.id}`}
              id={`grad-${f.id}`}
              x1={NUCLEUS.x} y1={NUCLEUS.y}
              x2={f.tip.x} y2={f.tip.y}
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={BLUE} stopOpacity="1" />
              <stop offset="100%" stopColor={BLUE_FAINT} stopOpacity="0.35" />
            </linearGradient>
          ))}

          <marker id="arrow" markerWidth="9" markerHeight="9" refX="7" refY="3" orient="auto">
            <path d="M0,0 L0,6 L9,3 z" fill={BLUE} />
          </marker>
        </defs>

        {/* Large radial nucleus atmosphere */}
        <circle cx={NUCLEUS.x} cy={NUCLEUS.y} r="180" fill="url(#nucleusGlow)" />

        {/* Gradient spokes: nucleus -> tip */}
        {features.map((f, i) => {
          const spokeLen = dist(NUCLEUS, f.tip);
          const delay = `${0.06 * i}s`;
          return (
            <line
              key={f.id}
              className="spoke"
              x1={NUCLEUS.x} y1={NUCLEUS.y}
              x2={f.tip.x} y2={f.tip.y}
              stroke={`url(#grad-${f.id})`}
              strokeWidth="1.6"
              strokeLinecap="round"
              style={{ "--spoke-len": spokeLen, "--spoke-delay": delay }}
            />
          );
        })}

        {/* Gather line nucleus -> Squeekit */}
        <line
          className="gather-line"
          x1={NUCLEUS.x} y1={NUCLEUS.y}
          x2={SQUEEKIT.x - 6} y2={SQUEEKIT.y}
          stroke={BLUE}
          strokeWidth="2"
          strokeLinecap="round"
          markerEnd="url(#arrow)"
        />

        {/* Nucleus dot */}
        <circle className="nucleus-ring" cx={NUCLEUS.x} cy={NUCLEUS.y} r="11" fill={BLUE} fillOpacity="0.1" />
        <circle className="nucleus-ring" cx={NUCLEUS.x} cy={NUCLEUS.y} r="5" fill={BLUE} />

        {/* Feature labels at tip of each spoke */}
        {features.map((f, i) => {
          const lines = f.label;
          const lineH = 22;
          const totalH = lines.length * lineH;
          const startY = f.tip.y + f.labelOffset.y - (totalH / 2) + lineH / 2;
          const labelX = f.tip.x + f.labelOffset.x;
          const labelDelay = `${0.06 * i + 0.35}s`;

          return (
            <text
              key={f.id}
              className="feat-label"
              textAnchor={f.anchor}
              fill={DARK}
              fontSize="17.5"
              fontFamily="'DM Sans', sans-serif"
              fontWeight="500"
              letterSpacing="0.005em"
              style={{ "--label-delay": labelDelay }}
            >
              {lines.map((line, li) => (
                <tspan key={li} x={labelX} y={startY + li * lineH}>
                  {line}
                </tspan>
              ))}
            </text>
          );
        })}

        {/* Squeekit brand name */}
        <text
          className="squeekit-word"
          x={SQUEEKIT.x + 14}
          y={SQUEEKIT.y + 14}
          textAnchor="start"
          fill={BLUE}
          fontSize="48"
          fontFamily="'DM Sans', sans-serif"
          fontWeight="600"
          letterSpacing="-0.025em"
        >
          Squeekit
        </text>
      </svg>
    </div>
  );
}
